import { Component, ReactNode } from "react";
import ErrorComponent from "@app/components/errorState";
import { ReactComponent as UnexpectedErrorIconSVG } from "@app/assets/unexpected-error.svg";
import { ReactComponent as UnexpectedErrorOutlineIconSVG } from "@app/assets/unexpected-error-outline.svg";
import { ReactComponent as NoInternetIconSVG } from "@app/assets/no-internet.svg";
import { ReactComponent as NoInternetOutlineIconSVG } from "@app/assets/no-internet-outline.svg";
import { isOnline } from "@app/utils/common";
import { ERROR_SCREEN } from "@app/utils/constants";

type ErrorType = keyof typeof ERROR_SCREEN;

interface State {
    hasError: boolean;
    online: boolean;
    errorType: ErrorType;
}

interface Props {
    children: ReactNode;
}

// Created Class components to use error
// This component with display default error page on network issue and code issue
class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            hasError: false,
            online: true,
            errorType: "UNEXPECTED_ERROR",
        };
    }

    componentDidMount(): void {
        // to check network connection on every 10 seconds
        setInterval(async () => {
            const online = await isOnline();
            const errorType = online ? this.state.errorType : "NO_INTERNET";
            this.setState({
                hasError: false,
                online,
                errorType,
            });
        }, 10000);

        window.addEventListener("tokenInvalid", this.handleTokenInvalid);
    }

    componentWillUnmount() {
        window.removeEventListener("tokenInvalid", this.handleTokenInvalid);
    }

    handleTokenInvalid = () => {
        this.setState({
            hasError: true,
            errorType: "INVALID_TOKEN",
        });
    };

    static getDerivedStateFromError() {
        return {
            hasError: true,
            errorType: "UNEXPECTED_ERROR",
        };
    }

    render() {
        const { hasError, online, errorType } = this.state;

        const svgErrorIcon = !online ? NoInternetIconSVG : UnexpectedErrorIconSVG;
        const svgErrorOutlineIcon = !online
            ? NoInternetOutlineIconSVG
            : UnexpectedErrorOutlineIconSVG;

        if (!online || hasError) {
            return (
                <ErrorComponent
                    SvgErrorIcon={svgErrorIcon}
                    SvgErrorOutlineIcon={svgErrorOutlineIcon}
                    header={ERROR_SCREEN[errorType].HEADER_TEXT}
                    message={ERROR_SCREEN[errorType].DETAILED_TEXT}
                    isErrorPage={true}
                />
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
